import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
import { useCompleteLoginRecovery, useConfirmAuthenticatorActivation, useDeactivateAuthenticator, useRecoverAuthenticatorFromWithdrawal, useSendLoginRecoveryOtp, useSendTwoFactorOtp, useStartAuthenticatorActivation, useSwitchTwoFactorMethod, useTwoFactorState, useVerifyLoginRecoveryOtp } from "./service";
import { SECURITY_TYPE_VALUES, expiresAtFromTtl, getTwoFactorErrorKind, isCodeComplete, queueRecoveryToast } from "./helpers";
import type { AuthenticatorActivationStep, GoogleAuthenticatorData, LoginRecoveryTokens, TwoFactorErrorKind, TwoFactorMethod, TwoFactorOtpResponse, TwoFactorSuccessEvent, TwoFactorTarget } from "@/app/components/Dashboard/Security/security.interface";
type PendingAction = {
  kind: "switch";
  target: TwoFactorTarget;
  method: TwoFactorMethod;
} | {
  kind: "deactivate";
};
interface FlowOptions {
  onSuccess?: (event: TwoFactorSuccessEvent) => void;
}

/** The SMS code steps keep the `token` (tracking id) to send back with the code and count down to `expiresAt`. */
const useSentOtp = () => {
  const $ = _c(5);
  const [trackingId, setTrackingId] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = res => {
      setTrackingId(res.token);
      setExpiresAt(expiresAtFromTtl(res.ttl));
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const remember = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setTrackingId("");
      setExpiresAt(null);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const forget = t1;
  let t2;
  if ($[2] !== expiresAt || $[3] !== trackingId) {
    t2 = {
      trackingId,
      expiresAt,
      remember,
      forget
    };
    $[2] = expiresAt;
    $[3] = trackingId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/**
 * Activation: download -> SMS OTP -> QR/seed -> authenticator code (PRD 7.2).
 * Never asks for a password; the seed is only requested after the SMS OTP is accepted.
 * Success closes the flow and the page shows the toast (Figma "Success").
 */
export const useAuthenticatorActivation = ({
  onSuccess
}: FlowOptions = {}) => {
  const [step, setStep] = useState<AuthenticatorActivationStep | null>(null);
  const [code, setCode] = useState("");
  const otp = useSentOtp();
  const [seedData, setSeedData] = useState<GoogleAuthenticatorData | null>(null);
  const [errorKind, setErrorKind] = useState<TwoFactorErrorKind | null>(null);
  const sendOtp = useSendTwoFactorOtp();
  const start = useStartAuthenticatorActivation();
  const confirm = useConfirmAuthenticatorActivation();
  const reset = useCallback(() => {
    setStep(null);
    setCode("");
    setSeedData(null);
    otp.forget();
    setErrorKind(null);
  }, [otp.forget]);
  const goTo = (next: AuthenticatorActivationStep) => {
    setCode("");
    setErrorKind(null);
    setStep(next);
  };
  const requestOtp = () => sendOtp.mutate("activation", {
    onSuccess: otp.remember
  });
  const next_0 = () => {
    if (step === 1) {
      goTo(2);
      requestOtp();
    } else if (step === 3) goTo(4);
  };
  const back = () => {
    if (step === 4) goTo(3);else if (step === 3) {
      // the OTP was already accepted, so going back re-sends a fresh one
      goTo(2);
      requestOtp();
    } else if (step === 2) goTo(1);
  };
  const submitCode = () => {
    if (!isCodeComplete(code)) return;
    const onError = (error: unknown) => setErrorKind(getTwoFactorErrorKind(error));
    if (step === 2) {
      start.mutate({
        tracking_id: otp.trackingId,
        code
      }, {
        onSuccess: data => {
          setSeedData(data);
          goTo(3);
        },
        onError
      });
    } else if (step === 4) {
      confirm.mutate(code, {
        onSuccess: () => {
          reset();
          onSuccess?.({
            kind: "activated"
          });
        },
        onError
      });
    }
  };
  return {
    step,
    code,
    setCode: (value: string) => {
      setCode(value);
      setErrorKind(null);
    },
    expiresAt: otp.expiresAt,
    seedData,
    errorKind,
    isSubmitting: start.isPending || confirm.isPending,
    open: () => goTo(1),
    next: next_0,
    back,
    submitCode,
    resendOtp: requestOtp,
    close: reset
  };
};

/** Login/withdrawal switch and voluntary removal: only the CURRENT method's code (PRD 5.3). */
export const useCurrentMethodCodeFlow = t0 => {
  const $ = _c(43);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onSuccess
  } = t1;
  const {
    data: state
  } = useTwoFactorState();
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const otp = useSentOtp();
  const [errorKind, setErrorKind] = useState(null);
  const sendOtp = useSendTwoFactorOtp();
  const switchMethod = useSwitchTwoFactorMethod();
  const deactivate = useDeactivateAuthenticator();
  let t2;
  if ($[2] !== state) {
    t2 = action => {
      if (!state) {
        return null;
      }
      if (action.kind === "deactivate") {
        return "authenticator";
      }
      return action.target === "login" ? state.loginMethod : state.withdrawalMethod;
    };
    $[2] = state;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const methodFor = t2;
  let t3;
  if ($[4] !== methodFor || $[5] !== pending) {
    t3 = pending ? methodFor(pending) : null;
    $[4] = methodFor;
    $[5] = pending;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const currentMethod = t3;
  let t4;
  if ($[7] !== methodFor || $[8] !== otp.remember || $[9] !== sendOtp) {
    t4 = action_0 => {
      if (action_0.kind !== "switch" || methodFor(action_0) !== "sms") {
        return;
      }
      sendOtp.mutate(action_0.target, {
        onSuccess: otp.remember
      });
    };
    $[7] = methodFor;
    $[8] = otp.remember;
    $[9] = sendOtp;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const requestOtp = t4;
  let t5;
  if ($[11] !== otp || $[12] !== requestOtp || $[13] !== state) {
    t5 = action_1 => {
      if (!state) {
        return;
      }
      setCode("");
      setErrorKind(null);
      otp.forget();
      setPending(action_1);
      requestOtp(action_1);
    };
    $[11] = otp;
    $[12] = requestOtp;
    $[13] = state;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const begin = t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setPending(null);
      setCode("");
      setErrorKind(null);
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const close = t6;
  let t7;
  if ($[16] !== code || $[17] !== currentMethod || $[18] !== deactivate || $[19] !== onSuccess || $[20] !== otp.trackingId || $[21] !== pending || $[22] !== state || $[23] !== switchMethod) {
    t7 = () => {
      if (!pending || !state || !isCodeComplete(code)) {
        return;
      }
      const onError = e => setErrorKind(getTwoFactorErrorKind(e));
      if (pending.kind === "deactivate") {
        deactivate.mutate(code, {
          onSuccess: () => {
            close();
            onSuccess?.({
              kind: "deactivated"
            });
          },
          onError
        });
      } else {
        const {
          target,
          method
        } = pending;
        switchMethod.mutate({
          login_otp_type: SECURITY_TYPE_VALUES[target === "login" ? method : state.loginMethod],
          withdraw_otp_type: SECURITY_TYPE_VALUES[target === "withdrawal" ? method : state.withdrawalMethod],
          tracking_id: currentMethod === "sms" ? otp.trackingId : undefined,
          code
        }, {
          onSuccess: () => {
            close();
            onSuccess?.({
              kind: "switched",
              target,
              method
            });
          },
          onError
        });
      }
    };
    $[16] = code;
    $[17] = currentMethod;
    $[18] = deactivate;
    $[19] = onSuccess;
    $[20] = otp.trackingId;
    $[21] = pending;
    $[22] = state;
    $[23] = switchMethod;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const submit = t7;
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = value => {
      setCode(value);
      setErrorKind(null);
    };
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = switchMethod.isPending || deactivate.isPending;
  let t10;
  let t11;
  if ($[26] !== begin) {
    t10 = (target_0, method_0) => begin({
      kind: "switch",
      target: target_0,
      method: method_0
    });
    t11 = () => begin({
      kind: "deactivate"
    });
    $[26] = begin;
    $[27] = t10;
    $[28] = t11;
  } else {
    t10 = $[27];
    t11 = $[28];
  }
  let t12;
  if ($[29] !== pending || $[30] !== requestOtp) {
    t12 = () => pending && requestOtp(pending);
    $[29] = pending;
    $[30] = requestOtp;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== code || $[33] !== currentMethod || $[34] !== errorKind || $[35] !== otp.expiresAt || $[36] !== pending || $[37] !== submit || $[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t9) {
    t13 = {
      pending,
      currentMethod,
      code,
      setCode: t8,
      expiresAt: otp.expiresAt,
      errorKind,
      isSubmitting: t9,
      beginSwitch: t10,
      beginDeactivate: t11,
      submit,
      resendOtp: t12,
      close
    };
    $[32] = code;
    $[33] = currentMethod;
    $[34] = errorKind;
    $[35] = otp.expiresAt;
    $[36] = pending;
    $[37] = submit;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t9;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
type RecoveryPhase = "otp" | "password";

/**
 * Forgot Authenticator during login (PRD 8): SMS OTP first, and only after it is accepted
 * the password step appears. A wrong OTP or password changes nothing.
 * `loginTrackingId` is the `token` of the mobile step; each later step returns the token for the next one.
 */
export const useLoginRecovery = (mobile, loginTrackingId, onRecovered) => {
  const $ = _c(31);
  const [phase, setPhase] = useState("otp");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const otp = useSentOtp();
  const [recoveryTrackingId, setRecoveryTrackingId] = useState("");
  const [errorKind, setErrorKind] = useState(null);
  const sendOtp = useSendLoginRecoveryOtp();
  const verifyOtp = useVerifyLoginRecoveryOtp();
  const complete = useCompleteLoginRecovery();
  let t0;
  if ($[0] !== loginTrackingId || $[1] !== mobile || $[2] !== otp.remember || $[3] !== sendOtp) {
    t0 = () => sendOtp.mutate({
      mobile,
      tracking_id: loginTrackingId
    }, {
      onSuccess: otp.remember
    });
    $[0] = loginTrackingId;
    $[1] = mobile;
    $[2] = otp.remember;
    $[3] = sendOtp;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const sendCode = t0;
  let t1;
  if ($[5] !== sendCode) {
    t1 = () => {
      setPhase("otp");
      setCode("");
      setPassword("");
      setRecoveryTrackingId("");
      setErrorKind(null);
      sendCode();
    };
    $[5] = sendCode;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const start = t1;
  let t2;
  if ($[7] !== code || $[8] !== mobile || $[9] !== otp.trackingId || $[10] !== verifyOtp) {
    t2 = () => {
      if (!isCodeComplete(code) || verifyOtp.isPending) {
        return;
      }
      verifyOtp.mutate({
        mobile,
        tracking_id: otp.trackingId,
        code
      }, {
        onSuccess: res => {
          setRecoveryTrackingId(res.token);
          setErrorKind(null);
          setPhase("password");
        },
        onError: e => setErrorKind(getTwoFactorErrorKind(e))
      });
    };
    $[7] = code;
    $[8] = mobile;
    $[9] = otp.trackingId;
    $[10] = verifyOtp;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const submitOtp = t2;
  let t3;
  if ($[12] !== complete || $[13] !== mobile || $[14] !== onRecovered || $[15] !== password || $[16] !== recoveryTrackingId) {
    t3 = () => {
      if (!password || complete.isPending) {
        return;
      }
      complete.mutate({
        mobile,
        tracking_id: recoveryTrackingId,
        password
      }, {
        onSuccess: onRecovered,
        onError: e_0 => setErrorKind(getTwoFactorErrorKind(e_0))
      });
    };
    $[12] = complete;
    $[13] = mobile;
    $[14] = onRecovered;
    $[15] = password;
    $[16] = recoveryTrackingId;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const submitPassword = t3;
  let t4;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => {
      setCode(value);
      setErrorKind(null);
    };
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value_0 => {
      setPassword(value_0);
      setErrorKind(null);
    };
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = verifyOtp.isPending || complete.isPending;
  let t7;
  if ($[20] !== code || $[21] !== errorKind || $[22] !== otp.expiresAt || $[23] !== password || $[24] !== phase || $[25] !== sendCode || $[26] !== start || $[27] !== submitOtp || $[28] !== submitPassword || $[29] !== t6) {
    t7 = {
      phase,
      code,
      setCode: t4,
      password,
      setPassword: t5,
      expiresAt: otp.expiresAt,
      errorKind,
      isSubmitting: t6,
      start,
      submitOtp,
      submitPassword,
      resendOtp: sendCode
    };
    $[20] = code;
    $[21] = errorKind;
    $[22] = otp.expiresAt;
    $[23] = password;
    $[24] = phase;
    $[25] = sendCode;
    $[26] = start;
    $[27] = submitOtp;
    $[28] = submitPassword;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return t7;
};

/** Forgot from the withdrawal page (PRD 9): no code; Authenticator is removed, the hold starts, the user is logged out. */
export const useWithdrawalRecovery = onRecovered => {
  const $ = _c(6);
  const recover = useRecoverAuthenticatorFromWithdrawal();
  let t0;
  if ($[0] !== onRecovered || $[1] !== recover) {
    t0 = () => recover.mutate(undefined, {
      onSuccess: () => {
        queueRecoveryToast();
        onRecovered();
      }
    });
    $[0] = onRecovered;
    $[1] = recover;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== recover.isPending || $[4] !== t0) {
    t1 = {
      isSubmitting: recover.isPending,
      confirm: t0
    };
    $[3] = recover.isPending;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};